/**
 * Task 8 (media images, 2026-09-04): `/media/:id`, the signed-in full-size
 * view a Halo note's image line links back to (services/ops's `formatDelta`
 * writes that link — see its own header comment for the exact URL shape).
 *
 * SIGN-IN GATED, like every other store-`page` route — `router.tsx` wraps
 * this in `RequireAuth` exactly like `/transcript/:id`, `/settings`, etc.
 * (`components/auth/AuthGate.tsx`). This page itself does no auth check of
 * its own; it trusts the route wrapper the same way `TranscriptPage` does.
 *
 * Loading/error/success states mirror `TranscriptPage`'s own three-state
 * shape (`transcript-loading` / `transcript-error` / the real content) —
 * same discipline, same reason: an id that resolves to nothing must say so
 * in plain text, never a silent blank page or a broken-image icon.
 */
import { useParams } from 'react-router-dom'
import { useMediaBlob } from '../lib/useMediaBlob'

export function MediaPage() {
  const { id } = useParams<{ id: string }>()
  const { url, failed } = useMediaBlob(id ?? null)

  if (!id) {
    return <p className="p-4 text-[13px] text-mid">No media id given.</p>
  }

  if (failed) {
    return (
      <p className="p-4 text-[13px] text-mid" data-testid="media-page-error">
        Couldn&apos;t load this image. It may not exist, or you may not have access to it.
      </p>
    )
  }

  if (!url) {
    return (
      <p className="p-4 text-[13px] text-mid" data-testid="media-page-loading">
        Loading image…
      </p>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-page p-4">
      {/* Mobile-blowout law (Andrew, 2026-09-03 x2): NEVER an unconstrained
          intrinsic size. `max-h-screen`/`max-w-full` here play the same role
          Transcript.tsx's `max-h-[240px]` thumbnail cap plays for the
          transcript bubble — a full-size view still needs a ceiling, or a
          phone-camera original blows straight off a phone screen. */}
      <img
        data-testid="media-page-image"
        src={url}
        alt="Attached image"
        className="max-h-screen max-w-full rounded-[8px] object-contain"
      />
    </div>
  )
}
